<script setup lang="ts">
import Icon from '../icon/Icon.vue';
import type { IconName } from '../icon/Icon.types';

export interface InputProps {
  modelValue?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  icon?: IconName;
  type?: string;
  disabled?: boolean;
  error?: string;
  modal?: string;
  customClass?: string;
  help?: string;
}

defineOptions({
  inheritAttrs: false,
});

withDefaults(defineProps<InputProps>(), {
  modelValue: '',
  type: 'text',
  disabled: false,
  required: false,
});

defineEmits<{
  'update:modelValue': [value: string];
}>();
</script>

<template>
  <div class="flex flex-col gap-1">
    <label
      v-if="label"
      class="text-label font-bold uppercase tracking-wider text-on-surface-variant"
    >
      {{ label }}<span v-if="required" class="text-critical"> *</span>
    </label>
    <div
      :class="[
        'flex items-center gap-2 rounded-md p-4 border bg-white',
        'transition-colors duration-normal ease-default',
        customClass,
        modal,
        error
          ? 'border-critical'
          : 'border-transparent focus:border-primary focus-within:border-primary',
        disabled && 'opacity-50 pointer-events-none',
      ]"
    >
      <Icon v-if="icon" :name="icon" size="md" class="text-on-surface-muted" />
      <input
        v-bind="$attrs"
        :value="modelValue"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        class="mb-0.5 w-full bg-transparent text-body text-black placeholder:text-on-surface-muted outline-none dark:[&::-webkit-calendar-picker-indicator]:invert"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
      />
    </div>
    <span v-if="error" class="text-caption text-critical">{{ error }}</span>
    <span v-if="help" class="mt-sm text-xs text-on-surface-variant">
      {{ help }}
    </span>
  </div>
</template>
